import React from 'react';
import { Newspaper, Zap, Filter, Star } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { FeatureCard } from '../components/ui/FeatureCard';

export const NewsHunterAiPage: React.FC = () => {
    usePageMetadata({
        title: "NewsHunter AI | Radar Trendów i Wiralowych Treści",
        description: "Wyprzedź konkurencję! NewsHunter AI to inteligentny agregator, który znajduje wiralowe treści, zanim staną się popularne. Idealne dla Twoich social media.",
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "NewsHunter AI (Monitoring Trendów)",
            "serviceType": "Agregacja i Analiza Wiadomości",
            "provider": {
                "@type": "Organization",
                "name": "BiznesBezKlikania.pl"
            },
            "description": "Inteligentny agregator newsów, który analizuje tysiące źródeł i znajduje wiralowe treści, zanim staną się popularne.",
            "url": "https://biznesbezklikania.pl/rozwiazania/newshunter-ai"
        }
    });

    const features = [
        { icon: <Zap className="w-8 h-8" />, title: 'Analiza Emocjonalna', description: 'System wykrywa słowa kluczowe wywołujące silne emocje (strach, zaskoczenie, radość), które napędzają udostępnienia.' },
        { icon: <Filter className="w-8 h-8" />, title: 'Wykrywanie Trendów', description: 'AI koreluje tematy z danymi z Google Trends i social media, aby ocenić, czy dany temat zyskuje na popularności.' },
        { icon: <Star className="w-8 h-8" />, title: 'Scoring Strukturalny', description: 'Tytuły w formie pytań, listy, czy duże liczby otrzymują dodatkowe punkty za udowodnioną skuteczność w przyciąganiu uwagi.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="mixed" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                            <Newspaper className="w-10 h-10 text-white" />
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 dark:from-orange-400 dark:via-red-400 dark:to-orange-400 bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            NewsHunter AI
                        </h1>
                        <p className="text-sm font-semibold text-orange-500 dark:text-orange-400 uppercase tracking-wider mb-4">
                            Monitoring Trendów
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Znajdź newsa, który stanie się wiralem, zanim zrobi to Twoja konkurencja. Nasz system analizuje tysiące źródeł i przewiduje, co za chwilę będzie na ustach wszystkich.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="newshunter-ai"
                            triggerLabel="Wyprzedź konkurencję"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-xl hover:shadow-xl hover:shadow-orange-500/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Features Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 max-w-5xl relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Jak AI Przewiduje Wiralowość Treści?
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4">
                            Nasz unikalny silnik scoringowy ocenia każdy news na podstawie 15 kryteriów.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/newshunter-ai" />
        </>
    );
};
